import apiClient from "./client";
import { unwrapApiResponse } from "../utils/apiError";
import { saveAxiosFile } from "../utils/download";

async function unwrap(request) {
  const response = await request;
  return unwrapApiResponse(response);
}

function createFileFormData(file) {
  const formData = new FormData();
  formData.append("file", file);
  return formData;
}

async function downloadFile(request, fallbackName) {
  const response = await request;
  saveAxiosFile(response, fallbackName);
}

function sanitizeQuestionPayload(data) {
  if (!data || typeof data !== "object") {
    return data;
  }

  const payload = { ...data };
  const questionType = payload.type;

  delete payload.textDirection;
  delete payload.optionsTextDirection;
  delete payload.dir;
  delete payload.direction;
  delete payload.isLtr;

  if (Array.isArray(payload.options)) {
    payload.options = payload.options.map((option) => {
      if (option == null || typeof option !== "object") {
        return option;
      }

      const next = { ...option };
      delete next.textDirection;
      delete next.dir;
      delete next.direction;
      delete next.isLtr;
      return next;
    });
  }

  if (questionType === "descriptive" || questionType === "instructional") {
    delete payload.options;
    delete payload.correctOptionIndex;
    delete payload.correctOptionId;
  }

  return payload;
}

function sanitizeGradePayload(data) {
  if (!data || typeof data !== "object") {
    return data;
  }

  if (!Array.isArray(data.answers)) {
    return data;
  }

  return {
    ...data,
    answers: data.answers.map(({ answerId, ...answer }) => answer),
  };
}

export const examAPI = {
  getAll: (params) => unwrap(apiClient.get("/teacher/exams", { params })),

  getById: (id) => unwrap(apiClient.get(`/teacher/exams/${id}`)),

  create: (data) => unwrap(apiClient.post("/teacher/exams", data)),

  update: (id, data) => unwrap(apiClient.patch(`/teacher/exams/${id}`, data)),

  delete: (id) => unwrap(apiClient.delete(`/teacher/exams/${id}`)),

  updateSettings: (id, data) => unwrap(apiClient.patch(`/teacher/exams/${id}/settings`, data)),

  publish: (id) => unwrap(apiClient.post(`/teacher/exams/${id}/publish`)),

  activate: (id) => unwrap(apiClient.post(`/teacher/exams/${id}/activate`)),

  close: (id) => unwrap(apiClient.post(`/teacher/exams/${id}/close`)),

  finish: (id) => unwrap(apiClient.post(`/teacher/exams/${id}/finish`)),

  getReport: (id) => unwrap(apiClient.get(`/teacher/exams/${id}/report`)),

  addQuestion: (id, data) =>
    unwrap(apiClient.post(`/teacher/exams/${id}/questions`, sanitizeQuestionPayload(data))),

  reorderQuestions: (id, questionIds) =>
    unwrap(apiClient.patch(`/teacher/exams/${id}/questions/reorder`, { questionIds })),

  updateQuestion: (id, questionId, data) =>
    unwrap(
      apiClient.patch(
        `/teacher/exams/${id}/questions/${questionId}`,
        sanitizeQuestionPayload(data)
      )
    ),

  deleteQuestion: (id, questionId) =>
    unwrap(apiClient.delete(`/teacher/exams/${id}/questions/${questionId}`)),

  uploadQuestionAttachment: (id, questionId, file) =>
    unwrap(
      apiClient.post(
        `/teacher/exams/${id}/questions/${questionId}/attachments`,
        createFileFormData(file),
        { headers: { "Content-Type": "multipart/form-data" } }
      )
    ),

  deleteQuestionAttachment: (id, questionId, attachmentId) =>
    unwrap(
      apiClient.delete(`/teacher/exams/${id}/questions/${questionId}/attachments/${attachmentId}`)
    ),

  downloadQuestionAttachment: (id, questionId, attachmentId, filename = "exam-question-attachment") =>
    downloadFile(
      apiClient.get(
        `/teacher/exams/${id}/questions/${questionId}/attachments/${attachmentId}/download`,
        { responseType: "arraybuffer" }
      ),
      filename
    ),

  uploadQuestionOptionAttachment: (id, questionId, optionId, file) =>
    unwrap(
      apiClient.post(
        `/teacher/exams/${id}/questions/${questionId}/options/${optionId}/attachment`,
        createFileFormData(file),
        { headers: { "Content-Type": "multipart/form-data" } }
      )
    ),

  deleteQuestionOptionAttachment: (id, questionId, optionId) =>
    unwrap(
      apiClient.delete(
        `/teacher/exams/${id}/questions/${questionId}/options/${optionId}/attachment`
      )
    ),

  getSubmissions: (id) => unwrap(apiClient.get(`/teacher/exams/${id}/submissions`)),

  getSubmission: (id, submissionId) =>
    unwrap(apiClient.get(`/teacher/exams/${id}/submissions/${submissionId}`)),

  gradeSubmission: (id, submissionId, data) =>
    unwrap(
      apiClient.patch(
        `/teacher/exams/${id}/submissions/${submissionId}/grade`,
        sanitizeGradePayload(data)
      )
    ),
};
